import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import type { ParameterCatalog } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-input-year-range",
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: "./form-input-year-range.html",
  styleUrl: "./form-input-year-range.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputYearRangeComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputYearRangeComponent),
    },
  ],
})
export class FormInputYearRangeComponent implements ControlValueAccessor {
  readonly languageService = inject(LanguageService);
  readonly AdaaHelper = AdaaHelper;

  readonly inputControl = new FormControl();
  fromValue: number;
  toValue: number;
  tempFromValue: number;
  tempToValue: number;

  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string | null) => void;

  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly fromLabel = input<string>("common.tables.range.from");
  readonly toLabel = input<string>("common.tables.range.to");
  readonly fromPlaceholder = input<string>("");
  readonly toPlaceholder = input<string>("");
  readonly placeholder = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly setDefaultValue = input<string>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  constructor() {
    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) this.writeValue(value);
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
          });
        }
      }
    });
  }

  public writeValue(obj: string): void {
    if (obj === null) {
      this.inputControl.reset();
      return;
    }

    const valueArr = obj.split("-");
    if (!AdaaHelper.isDefined(valueArr)) return;

    if (AdaaHelper.isDefined(valueArr[0])) {
      const timestamp = parseFloat(valueArr[0]);
      this.tempFromValue = new Date(timestamp).getFullYear();
    }

    if (AdaaHelper.isDefined(valueArr[1])) {
      const timestamp = parseFloat(valueArr[1]);
      this.tempToValue = new Date(timestamp).getFullYear();
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return isRequired && { required: true };
  }

  public tempValueChanged(event: Event, isFrom: boolean): void {
    const year = parseInt((event.target as HTMLInputElement).value);

    if (isFrom) {
      this.tempFromValue = year;
    } else {
      this.tempToValue = year;
    }

    this.valueChanged(event, isFrom);
  }

  public valueChanged(event: Event, isFrom: boolean): void {
    const year = parseInt((event.target as HTMLInputElement).value);

    if (isFrom) {
      const firstDay = new Date(year, 0, 1);
      this.fromValue = firstDay.getTime();
    } else {
      const lastDay = new Date(year, 11, 31);
      this.toValue = lastDay.getTime();
    }

    const value = `${this.fromValue != null ? this.fromValue : ""} - ${this.toValue != null ? this.toValue : ""}`;
    this._onChange(value);
    this.inputChanges.emit(value);
  }
}
